import React, { useEffect } from 'react';
import { useForm, SubmitHandler, FieldValues } from 'react-hook-form';
import { Button } from 'app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'app/components/ui/dialog';
import { useBlogsSlice } from '../../slice';
import Swal from 'sweetalert2';

interface CategoryDialogProps {
  isOpen: boolean;
  onClose: any;
  categoryToEdit?: any;
}

const CategoryDialog: React.FC<CategoryDialogProps> = ({
  isOpen,
  onClose,
  categoryToEdit = null,
}) => {
  const isEditMode = !!categoryToEdit;

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FieldValues>();

  const { useAddCategoriesMutation, useUpdateCategoriesMutation } =
    useBlogsSlice();

  const [addCategory, { isLoading: isAdding, data: added, error: addError }] =
    useAddCategoriesMutation();

  const [
    updateCategory,
    { isLoading: isUpdating, data: updated, error: updateError },
  ] = useUpdateCategoriesMutation();

  // Pre-fill form in edit mode
  useEffect(() => {
    if (isEditMode && categoryToEdit) {
      setValue('name', categoryToEdit.name);
    } else {
      reset();
    }
  }, [isEditMode, categoryToEdit, setValue, reset]);

  // Handle success/error alerts
  useEffect(() => {
    if (added || updated) {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: isEditMode
          ? 'Category updated successfully!'
          : 'Category added successfully!',
        showConfirmButton: false,
        timer: 1500,
      });
      reset();
      onClose();
    }

    if (addError || updateError) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to save category. Please try again.',
      });
    }
  }, [added, updated, addError, updateError]);

  const handleSave: SubmitHandler<FieldValues> = async data => {
    const payload = {
      name: data.name,
    };

    if (isEditMode && categoryToEdit) {
      await updateCategory({ category_id: categoryToEdit.id, ...payload });
    } else {
      await addCategory(payload);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit Category' : 'Add Category'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleSave)} className="space-y-4 p-5">
          {/* Name Input */}
          <div className="mb-4">
            <label
              htmlFor="category-name"
              className="block text-sm font-medium mb-2"
            >
              Category Name
            </label>
            <input
              id="category-name"
              {...register('name', { required: 'Category Name is required' })}
              className="w-full p-2 border rounded-md"
            />
            {errors.name && (
              <span className="text-red-500 text-sm">
                {errors.name.message as string}
              </span>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isAdding || isUpdating}>
              {isEditMode ? 'Save Changes' : 'Add Category'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryDialog;
